/**
 * Client-side localStorage utilities for watchlist management
 * Used for anonymous (not logged-in) users
 */

const WATCHLIST_STORAGE_KEY = "locationguide24_watchlist";

export interface WatchlistStorageItem {
  locationId: number;
  addedAt: string; // ISO timestamp
}

/**
 * Get all watchlist items from localStorage
 */
export function getWatchlistFromStorage(): WatchlistStorageItem[] {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const stored = localStorage.getItem(WATCHLIST_STORAGE_KEY);
    if (!stored) {
      return [];
    }
    return JSON.parse(stored) as WatchlistStorageItem[];
  } catch (error) {
    console.error("Error reading watchlist from localStorage:", error);
    return [];
  }
}

/**
 * Add a location to localStorage watchlist
 */
export function addToWatchlistStorage(locationId: number): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const items = getWatchlistFromStorage();
    
    // Check if already exists
    if (items.some((item) => item.locationId === locationId)) {
      return;
    }

    items.push({
      locationId,
      addedAt: new Date().toISOString(),
    });

    localStorage.setItem(WATCHLIST_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error("Error adding to watchlist storage:", error);
  }
}

/**
 * Remove a location from localStorage watchlist
 */
export function removeFromWatchlistStorage(locationId: number): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const items = getWatchlistFromStorage();
    const filtered = items.filter((item) => item.locationId !== locationId);
    localStorage.setItem(WATCHLIST_STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error("Error removing from watchlist storage:", error);
  }
}

/**
 * Check if a location is in localStorage watchlist
 */
export function isInWatchlistStorage(locationId: number): boolean {
  const items = getWatchlistFromStorage();
  return items.some((item) => item.locationId === locationId);
}

/**
 * Get location IDs from localStorage watchlist
 */
export function getWatchlistLocationIds(): number[] {
  const items = getWatchlistFromStorage();
  return items.map((item) => item.locationId);
}

/**
 * Clear all watchlist items from localStorage
 */
export function clearWatchlistStorage(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(WATCHLIST_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing watchlist storage:", error);
  }
}

